<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :unit-options="unitOptions"
          :unit-filter.sync="filters.units"
          :user-options="userOptions"
          :country-options="countryOptions"
          :municipality-options="municipalityOptions"
          :status-options="statusOptions"
          :user-filter.sync="filters.users"
          :status-filter.sync="filters.status"
          :responsable-filter.sync="filters.responsable"
          :assigned-filter.sync="filters.assigned"
          :start-date.sync="filters.startDate"
          :end-date.sync="filters.endDate"
          :country-filter.sync="filters.countries"
          :municipality-filter.sync="filters.municipalities"
          :rems-filter.sync="filters.rems"
          :sirh-filter.sync="filters.sirh"
        />
      </template>
    </filter-modal>

    <b-card class="mx-2" no-body>
      <b-row class="m-2">
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label class="text-capitalize">{{ $t("table.show") }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block mx-50"
          />
          <label class="text-capitalize">{{ $t("table.entries") }}</label>
        </b-col>

        <b-col cols="12" md="6">
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('table.search') + '...'"
            />
            <b-button
              variant="secondary"
              @click="openAdd()"
              v-if="$can('create', 'RiskEvaluation')"
            >
              <span class="text-nowrap text-capitalize">
                {{ $t("actions.add") + " " + $tc("risk.evaluation.title", 1) }}
              </span>
            </b-button>
            <b-button
              @click="exportExcel()"
              variant="success"
              class="text-capitalize ml-1"
              type="button"
              :disabled="buttonExport"
            >
              <feather-icon icon="DownloadCloudIcon" size="15" />
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>
        <template #cell(last_status.name)="data">
          <span>
            {{ formatStatus(data.item.last_status && data.item.last_status.name) }}</span
          >
        </template>
        <template #cell(assigned_to)="data">
          <div>
            <div>{{ getUserPrimary(data.item.assigned_to) }}</div>
            <small class="text-muted">{{
              getUserSecondary(data.item.assigned_to)
            }}</small>
          </div>
        </template>
        <template #cell(in_review_by)="data">
          <div>
            <div>{{ getUserPrimary(assignedDisplay(data.item)) }}</div>
            <small class="text-muted">{{
              getUserSecondary(assignedDisplay(data.item))
            }}</small>
          </div>
        </template>
        <template #cell(accepted_or_cancelled_by)="data">
          <div>
            <div>{{ getUserPrimary(data.item.accepted_or_cancelled_by) }}</div>
            <small class="text-muted">{{
              getUserSecondary(data.item.accepted_or_cancelled_by)
            }}</small>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="canShowStartForm(data.item)"
              @click="openStartFormFromRow(data.item)"
            >
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("simulacrum.start_form") }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="!canShowStartForm(data.item)"
              @click="showEditEvaluationModal(data.item)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.edit") }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
            @click="downloadPdfAndFilesLinear(data.item.id)"
            v-if="data.item.last_status && data.item.last_status.name == 'approved'"
            >
              <feather-icon icon="FileIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.download") }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="confirmDelete(data.item.id)"
              v-if="
                $can('cancel', 'RiskEvaluation') &&
                data.item.last_status && data.item.last_status.name !== 'canceled' &&
                !canShowStartForm(data.item)
              "
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.cancel") }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">
              {{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Modal: mounted always we have an evaluationId, and we pass just first time the snapshot/sources-->
    <ShowRiskFormModal
      v-if="modalEvaluationId"
      :key="modalEvaluationId"
      :risk-evaluation-id="modalEvaluationId"
      :template-snapshot="modalSnapshot"
      :template-source-ids="modalSources"
      @close="onRiskModalClose"
      @need-form-selection="openFormSelectionFromModal"
    />

    <!-- Wizard -->
    <risk-evaluation-wizard
      ref="riskEvaluationWizard"
      :prefill="wizardPrefill"
      @open-evaluation-modal="handleOpenEvaluation"
      @wizard-complete="onWizardComplete"
      @wizard-closed="onWizardClosed"
      @close="closeWizard"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BDropdown,
  BDropdownItem,
  BTable,
  BCollapse,
} from "bootstrap-vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import { ref, getCurrentInstance, onMounted, watch } from "@vue/composition-api";
import vSelect from "vue-select";
import Swal from "sweetalert2";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Pagination from "@/components/Pagination.vue";

import useList from "./useList";

import ShowRiskFormModal from "@/components/ShowRiskFormModal.vue";
import Filters from "./Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import RiskEvaluationWizard from "./RiskEvaluationWizard.vue";
import {
  canShowScheduledStartForm,
  buildRowFromFormSelectionPayload,
  resolveScheduledAssigneeDisplay,
} from "@/libs/risk/scheduledStartFormAccess";
import {
  ensureUnitLoadedForWizard,
  getUnitSnapshotForWizardPrefill,
} from "@/libs/risk/ensureUnitLoadedForWizard";

export default {
  name: "IndexRiskEvaluations",
  components: {
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BDropdown,
    BDropdownItem,
    BTable,
    BCollapse,
    Pagination,
    vSelect,
    ToastificationContent,
    ShowRiskFormModal,
    Filters,
    FilterModal,
    ButtonFilters,
    RiskEvaluationWizard,
  },
  setup() {
    const vm = getCurrentInstance().proxy;

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      unitOptions,
      userOptions,
      filters,
      filtersCount,
      buildFilters,
      countryOptions,
      municipalityOptions,
      clearSearchFilters,
    } = useList();

    const toast = useToast();
    const buttonExport = ref(false);
    const wizardPrefill = ref(null);

    const canShowStartForm = canShowScheduledStartForm;

    const openStartFormFromRow = async (row) => {
      const unitId = row.id_unit != null ? Number(row.id_unit) : row.unit && row.unit.id != null ? Number(row.unit.id) : null;
      if (unitId == null || !Number.isFinite(unitId)) return;
      const unitSnapshot = getUnitSnapshotForWizardPrefill(row, unitId);
      const lastStatusName =
        row.last_status && row.last_status.name != null ? String(row.last_status.name).trim() : "";
      wizardPrefill.value = {
        riskEvaluationId: Number(row.id),
        referenceNumber: row.reference_number || "",
        unitId,
        ...(unitSnapshot ? { unitSnapshot } : {}),
        ...(lastStatusName ? { existingRecordLastStatusName: lastStatusName } : {}),
      };
      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "risk-evaluation-wizard");
    };

    const onWizardClosed = () => {
      wizardPrefill.value = null;
    };

    const statusOptions = [
      { label: vm.$t("risk.status_schedule"), value: "scheduled" },
      { label: vm.$t("risk.status_in_progress"), value: "in_progress" },
      { label: vm.$t("risk.status_in_review"), value: "in_review" },
      { label: vm.$t("risk.status_approved"), value: "approved" },
      { label: vm.$t("risk.status_canceled"), value: "canceled" },
      { label: vm.$t("risk.status_reassigned"), value: "reassigned" },
    ];

    const exportExcel = async () => {
      try {
        buttonExport.value = true;
        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          sort_by: sortBy.value,
          sort_desc: isSortDirDesc.value ? 1 : 0,
          search: searchQuery.value,
          units: filters.value.units,
          users: filters.value.users,
          countries: filters.value.countries,
          municipalities: filters.value.municipalities,
          status_form: filters.value.status,
          responsable: filters.value.responsable,
          assigned: filters.value.assigned,
          date_from: filters.value.startDate,
          date_to: filters.value.endDate,
          rems: filters.value.rems,
          sirh: filters.value.sirh,
          action: "export",
        };

        const res = await axios.get("/v1/risk_evaluations", {
          params,
          responseType: "blob",
        });

        const cd = res.headers["content-disposition"];
        let filename = "risk_evaluations.xlsx";
        if (cd) {
          const m = cd.match(/filename=\"?([^\";]+)\"?/i);
          if (m) filename = decodeURIComponent(m[1]);
        }

        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.message || "Error al exportar",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        buttonExport.value = false;
      }
    };

    // Helpers to display user-like fields that may be string or object
    const getUserPrimary = (val) => {
      if (!val) return "-";
      if (typeof val === "string") return val || "-";
      return val.name || val.username || val.email || "-";
    };
    const getUserSecondary = (val) => {
      if (!val) return "";
      if (typeof val === "string") return "";
      return val.username || val.email || "";
    };

    const findUserById = (id) => {
      const users = vm.$store.getters["user/getItems"] || [];
      return users.find((u) => Number(u.id) === Number(id)) || null;
    };
    const assignedDisplay = (item) =>
      resolveScheduledAssigneeDisplay(item, findUserById) || item.in_review_by;

    //Modal state (id + snapshot/sources just for the first render)
    const modalEvaluationId = ref(null);
    const modalSnapshot = ref(null);
    const modalSources = ref(null);

    const showFilters = ref(false);
    const toggleFilters = () => {
      showFilters.value = !showFilters.value;
    };

    const resetModalState = () => {
      modalEvaluationId.value = null;
      modalSnapshot.value = null;
      modalSources.value = null;
    };

    const resetModalStateWhenFormModalHides = () => {
      const onHidden = (bvEvent, modalId) => {
        const hiddenId = modalId || bvEvent?.componentId;
        if (hiddenId !== "risk_form_modal") return;
        vm.$root.$off("bv::modal::hidden", onHidden);
        resetModalState();
      };
      vm.$root.$on("bv::modal::hidden", onHidden);
    };

    const formatStatus = (name) => {
      const raw = String(name || "");
      const norm = raw
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const aliases = { canceled: "cancelled" };
      const canonical = aliases[norm] || norm;

      const tryKeys = [
        `procedure_states.${canonical}`,
        `procedure_states.value.${canonical}`,
      ];

      for (const k of tryKeys) {
        const v = vm.$t ? vm.$t(k) : k;
        if (typeof v === "string" && v !== k) return v;
      }
      return raw;
    };

    //Modal from table (existing edition)
    const showEditEvaluationModal = async (evaluation) => {
      modalEvaluationId.value = evaluation.id;
      modalSnapshot.value = null; // existing we dont pass snapshot
      modalSources.value = null;

      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "risk_form_modal");

      // Cleans when it closes
      resetModalStateWhenFormModalHides();
    };

    //Open modal when wizard requires (new or existing in_progress)
    const handleOpenEvaluation = async ({ id, snapshot, sources }) => {
      modalEvaluationId.value = id;
      modalSnapshot.value = Array.isArray(snapshot) ? snapshot : null;
      modalSources.value = Array.isArray(sources) ? sources : null;
      await vm.$nextTick();

      //Waits to the wizard modal hidding and then shows the risk modal
      const onHidden = (bvEvent, modalId) => {
        const hiddenId = modalId || bvEvent?.componentId;
        if (hiddenId === "risk-evaluation-wizard") {
          vm.$root.$off("bv::modal::hidden", onHidden);
          vm.$root.$emit("bv::show::modal", "risk_form_modal");
        }
      };
      vm.$root.$on("bv::modal::hidden", onHidden);

      //Closes wizard (this will shot onHidden that is upstairs)
      vm.$root.$emit("bv::hide::modal", "risk-evaluation-wizard");
    };

    // Open by query param (?id=...)
    function getQueryParam(param) {
      if (vm.$route && vm.$route.query && vm.$route.query[param]) {
        return vm.$route.query[param];
      }
      const queryString = window.location.hash.split("?")[1];
      if (!queryString) return null;
      const params = new URLSearchParams(queryString);
      return params.get(param);
    }

    async function openEvaluationFromNotification() {
      if (getQueryParam("scheduledStart")) return;
      const idParam = getQueryParam("id");
      if (!idParam) return;

      modalEvaluationId.value = Number(idParam);
      modalSnapshot.value = null;
      modalSources.value = null;

      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "risk_form_modal");

      vm.$nextTick(() => {
        if (vm.$route && vm.$route.query && Object.keys(vm.$route.query).length) {
          vm.$router.replace({ path: vm.$route.path, query: {} });
        }
      });

      resetModalStateWhenFormModalHides();
    }

    async function openScheduledStartFromQuery() {
      if (!getQueryParam("scheduledStart")) return;
      const idParam = getQueryParam("id");
      const unitIdParam = getQueryParam("unitId");
      if (!idParam || !unitIdParam) return;
      const reference = getQueryParam("reference") || "";
      const unitId = Number(unitIdParam);
      const unit = await ensureUnitLoadedForWizard(unitId);
      if (!unit) {
        toast({
          component: ToastificationContent,
          props: {
            title: "No se encontró la instalación en el catálogo.",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        vm.$nextTick(() => {
          if (vm.$route && vm.$route.query && Object.keys(vm.$route.query).length) {
            vm.$router.replace({ path: vm.$route.path, query: {} });
          }
        });
        return;
      }
      const existingLastStatus = getQueryParam("existingLastStatus");
      const row = {
        id: Number(idParam),
        reference_number: reference,
        id_unit: unitId,
        unit,
        ...(existingLastStatus ? { last_status: { name: existingLastStatus } } : {}),
      };
      await openStartFormFromRow(row);
      vm.$nextTick(() => {
        if (vm.$route && vm.$route.query && Object.keys(vm.$route.query).length) {
          vm.$router.replace({ path: vm.$route.path, query: {} });
        }
      });
    }

    watch(
      () => {
        const q = vm.$route && vm.$route.query ? vm.$route.query : {};
        return `${q.scheduledStart || ""}|${q.id || ""}|${q.unitId || ""}`;
      },
      async () => {
        const q = vm.$route && vm.$route.query ? vm.$route.query : {};
        if (q.scheduledStart && q.id && q.unitId) await openScheduledStartFromQuery();
      },
      { immediate: true },
    );

    // reactive by changes in the query param id
    watch(
      () => (vm.$route && vm.$route.query ? vm.$route.query.id : null),
      async (newId, oldId) => {
        if (newId && newId !== oldId) {
          await openEvaluationFromNotification();
        }
      }
    );

    const confirmDelete = (evaluationId) => {
      Swal.fire({
        title: "¿Estás seguro de cancelar esta evaluación?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Cancelar",
        cancelButtonText: vm.$t("actions.exit") || "Salir",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          axios
            .post(`/v1/risk_evaluations/update_status/${evaluationId}`, {
              status: "canceled",
            })
            .then(() => {
              refetchData();
              toast({
                component: ToastificationContent,
                props: {
                  title: "Evaluación cancelada",
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
            });
        }
      });
    };

    const update = (val) => {
      currentPage.value = val;
    };

    const safe = (s) =>
      String(s || "")
        .replace(/[\/\\:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .trim();

    const delay = (ms) => new Promise((r) => setTimeout(r, ms));

    function saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }

    async function downloadOne(url, filename) {
      try {
        const res = await axios.get(url, { responseType: "blob" });
        const name = safe(filename || url.split("/").pop() || "archivo");
        saveBlob(res.data, name);
      } catch (e) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
      }
    }

    async function fetchPdfRiskEvaluation(evaluationId) {
      const res = await axios.get(`/v1/risk_evaluations/${evaluationId}`, {
        params: { is_pdf: true },
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      });

      const cd = res.headers["content-disposition"];
      let filename = `evaluacion_${evaluationId}.pdf`;
      if (cd) {
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);
        if (m) filename = decodeURIComponent(m[1]);
      }

      return { blob: res.data, filename: safe(filename) };
    }

    async function fetchDetailsForFilesRiskEvaluation(evaluationId) {
      const res = await axios.get(
        `/v1/risk_form_answers/get-form-by-evaluation/${evaluationId}`
      );
      return res.data || {};
    }

    function extractAllFileEntriesRiskEvaluation(payload) {
      let answers = payload;

      if (answers && typeof answers === "object" && answers.data) {
        answers = answers.data;
      }
      if (answers && typeof answers === "object" && answers.data) {
        answers = answers.data;
      }

      const out = [];
      if (!answers || typeof answers !== "object") return out;

      Object.entries(answers).forEach(([fieldName, value]) => {
        if (!Array.isArray(value)) return;

        value.forEach((file, idx) => {
          if (!file || !file.url) return;
          const url = file.url;
          const baseName = file.name || file.filename || `archivo_${idx + 1}`;
          const niceName = `${safe(fieldName)} - ${safe(baseName)}`;
          out.push({ url, name: niceName });
        });
      });

      return out;
    }

    async function downloadPdfAndFilesLinear(evaluationId) {
      try {
        const { blob: pdfBlob, filename: pdfName } = await fetchPdfRiskEvaluation(
          evaluationId
        );
        saveBlob(pdfBlob, pdfName);

        await delay(400);

        const details = await fetchDetailsForFilesRiskEvaluation(evaluationId);
        const files = extractAllFileEntriesRiskEvaluation(details);
        if (!files.length) return;

        for (let i = 0; i < files.length; i += 1) {
          const f = files[i];
          await downloadOne(f.url, f.name);
          await delay(300);
        }
      } catch (error) {
        try {
          const { blob, filename } = await fetchPdfRiskEvaluation(evaluationId);
          saveBlob(blob, filename);
        } catch (e2) {
          toast({
            component: ToastificationContent,
            props: {
              title: vm.$t("errors.download_failed") || "No se pudo descargar",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      }
    }

    const downloadPdf = async (evaluationId) => {
      try {
        const res = await axios.get(`/v1/risk_evaluations/${evaluationId}`, {
          params: { is_pdf: true },
          responseType: "blob",
          headers: { Accept: "application/pdf" },
        });

        const cd = res.headers["content-disposition"];
        let filename = `evaluacion_${evaluationId}.pdf`;
        if (cd) {
          const m = cd.match(/filename=\"?([^\";]+)\"?/i);
          if (m) filename = decodeURIComponent(m[1]);
        }

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("error", error);
      }
    };

    onMounted(() => {
      openEvaluationFromNotification();
    });

    // Wizard handlers
    function openAdd() {
      vm.$root.$emit("bv::show::modal", "risk-evaluation-wizard");
    }
    function closeWizard() {
      vm.$root.$emit("bv::hide::modal", "risk-evaluation-wizard");
    }
    function onWizardComplete() {
      refetchData();
    }
    function onRiskModalClose() {
      refetchData();
    }

    async function openFormSelectionFromModal(payload) {
      vm.$root.$emit("bv::hide::modal", "risk_form_modal");
      await openStartFormFromRow(buildRowFromFormSelectionPayload(payload));
    }

    return {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,

      // modal state + actions
      modalEvaluationId,
      modalSnapshot,
      modalSources,
      showEditEvaluationModal,
      onRiskModalClose,
      openFormSelectionFromModal,

      confirmDelete,
      update,
      buttonExport,
      exportExcel,
      showFilters,
      filters,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      toggleFilters,
      unitOptions,
      userOptions,
      countryOptions,
      municipalityOptions,

      openAdd,
      closeWizard,
      onWizardComplete,

      handleOpenEvaluation,
      downloadPdf,
      downloadPdfAndFilesLinear,
      formatStatus,
      getUserPrimary,
      getUserSecondary,
      assignedDisplay,
      statusOptions,

      wizardPrefill,
      canShowStartForm,
      openStartFormFromRow,
      onWizardClosed,
    };
  },
};
</script>
